
import React, { useMemo } from "react";
import { useMediaQuery, Box } from "@mui/material";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import Domains from "../../global/constants/domain";

interface Props {
    xAxis: any[];
    yAxis: any[];
    totalheight: number;
    horizontal: boolean;
    diffbarcolor?: boolean;
    barWidth?: number;
}

const GroupReport: React.FC<Props> = ({
    xAxis,
    yAxis,
    totalheight,
    horizontal,
    diffbarcolor = false,
    barWidth = 0,
}) => {
    const isSmallScreen = useMediaQuery("(max-width:600px)");

    const getBarColors = () => {
        const domain = window.location.hostname;

        if (domain.includes(Domains.ALDI)) {
            return diffbarcolor
                ? ["#F96E09", "#00B5DC"]
                : ["#001F79", "#FFBF44"];
        }

        if (domain.includes(Domains.BLOOMS)) {
            return ["#1C2B90", "#C7D92F"];
        }

        if (domain.includes(Domains.MM)) {
            return ["#7C9FB2", "#ADB792"];
        }

        return ["#7C9FB2", "#ADB792"];
    };

    const barColors = getBarColors();

    const chartData = useMemo(() => {
        const categories =
            Array.isArray(xAxis) && xAxis.length
                ? xAxis
                : ["No Data"];

        const values =
            Array.isArray(yAxis) && yAxis.length
                ? yAxis
                : [0];

        return categories.map((category, index) => ({
            category,
            value: Number(values[index] ?? 0),
        }));
    }, [xAxis, yAxis]);

    const CustomTooltip = ({
        active,
        payload,
    }: {
        active?: boolean;
        payload?: any[];
    }) => {
        if (!active || !payload?.length) {
            return null;
        }

        const value = payload[0]?.value;

        return (
            <div
                style={{
                    padding: "4px 8px",
                    background: barColors[1],
                    borderRadius: "6px",
                    color: "#000",
                    fontSize: "14px",
                    fontWeight: 500,
                }}
            >
                {value}
            </div>
        );
    };

    return (
        <Box
            className="rounded-xl"
            sx={{
                width: "100%",
                height: totalheight,
            }}
        >
            <ResponsiveContainer
                width="100%"
                height={totalheight}
            >
                <BarChart
                    data={chartData}
                    layout={horizontal ? "vertical" : "horizontal"}
                    margin={{
                        top: 5,
                        right: 10,
                        left: horizontal ? 10 : 0,
                        bottom: 5,
                    }}
                    barCategoryGap={
                        barWidth > 0
                            ? isSmallScreen
                                ? "0%"
                                : `0%`
                            : "20%"
                    }
                >
                    {/* <CartesianGrid
                        vertical={!horizontal}
                        horizontal={horizontal}
                        stroke="#EAEAEA"
                    /> */}

                    {horizontal ? (
                        <>
                            <XAxis
                                type="number"
                                axisLine
                                tickLine={false}
                                tick={{
                                    fill: "#000000",
                                }}
                                tickFormatter={(value) =>
                                    Number(value).toFixed(2)
                                }
                            />

                            <YAxis
                                type="category"
                                dataKey="category"
                                axisLine
                                tickLine={false}
                                tick={{
                                    fill: "#000000",
                                }}
                            />
                        </>
                    ) : (
                        <>
                            <XAxis
                                type="category"
                                dataKey="category"
                                axisLine
                                tickLine={false}
                                tick={{
                                    fill: "#000000",
                                }}
                            />

                            <YAxis
                                type="number"
                                axisLine
                                tickLine={false}
                                tick={{
                                    fill: "#000000",
                                }}
                                tickFormatter={(value) =>
                                    Number(value).toFixed(2)
                                }
                            />
                        </>
                    )}

                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={false}
                    />

                    <Bar
                        dataKey="value"
                        fill={barColors[0]}
                        maxBarSize={
                            barWidth > 0
                                ? isSmallScreen
                                    ? 10
                                    : barWidth
                                : undefined
                        }
                        radius={
                            isSmallScreen
                                ? 5
                                : horizontal
                                ? 15
                                : 20
                        }
                        isAnimationActive={false}
                    />
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
};

export default GroupReport;